export interface TextAnchor {
  startOffset?: number;
  endOffset?: number;
  text?: string;
  prefixText?: string;
  suffixText?: string;
}

export interface ResolvedTextAnchorMatch {
  start: number;
  end: number;
  text: string;
  confidence: number;
  mode: 'exact' | 'normalized' | 'fuzzy' | 'legacy';
}

interface Candidate {
  start: number;
  end: number;
  text: string;
}

interface NormalizedText {
  text: string;
  map: number[];
}

const DEFAULT_CONTEXT_WINDOW = 48;
const MIN_FUZZY_CONFIDENCE = 0.52;
const CONTEXT_WEIGHT = 0.12;
const LENGTH_WEIGHT = 0.1;

function normalizeForTokens(value: string | undefined): string {
  return (value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeWithMap(value: string): NormalizedText {
  let normalized = '';
  const map: number[] = [];
  let lastWasSpace = true;

  for (let index = 0; index < value.length; index += 1) {
    const rawChar = value[index] ?? '';
    const expanded = rawChar
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    for (const char of expanded) {
      const nextChar = /[a-z0-9]/.test(char) ? char : ' ';
      if (nextChar === ' ') {
        if (lastWasSpace) continue;
        normalized += ' ';
        map.push(index);
        lastWasSpace = true;
        continue;
      }

      normalized += nextChar;
      map.push(index);
      lastWasSpace = false;
    }
  }

  if (normalized.endsWith(' ')) {
    normalized = normalized.slice(0, -1);
    map.pop();
  }

  return { text: normalized, map };
}

function tokenize(value: string | undefined): Set<string> {
  return new Set(
    normalizeForTokens(value)
      .split(' ')
      .filter((token) => token.length >= 3),
  );
}

function computeJaccard(left: Set<string>, right: Set<string>): number {
  if (left.size === 0 || right.size === 0) return 0;

  let intersection = 0;
  for (const token of left) {
    if (right.has(token)) intersection += 1;
  }

  const union = new Set([...left, ...right]).size;
  return union === 0 ? 0 : intersection / union;
}

function findAllOccurrences(haystack: string, needle: string): number[] {
  if (!needle) return [];
  const matches: number[] = [];
  let fromIndex = 0;

  while (fromIndex <= haystack.length - needle.length) {
    const matchIndex = haystack.indexOf(needle, fromIndex);
    if (matchIndex === -1) break;
    matches.push(matchIndex);
    fromIndex = matchIndex + 1;
  }

  return matches;
}

function scoreContext(content: string, start: number, end: number, anchor: TextAnchor | undefined): number {
  const prefixTokens = tokenize(anchor?.prefixText);
  const suffixTokens = tokenize(anchor?.suffixText);

  const prefixScore = prefixTokens.size
    ? computeJaccard(prefixTokens, tokenize(content.slice(Math.max(0, start - DEFAULT_CONTEXT_WINDOW), start)))
    : 0;
  const suffixScore = suffixTokens.size
    ? computeJaccard(suffixTokens, tokenize(content.slice(end, Math.min(content.length, end + DEFAULT_CONTEXT_WINDOW))))
    : 0;

  const prefixWeight = prefixTokens.size ? 1 : 0;
  const suffixWeight = suffixTokens.size ? 1 : 0;
  const divisor = prefixWeight + suffixWeight;
  if (divisor === 0) return 0;
  return (prefixScore + suffixScore) / divisor;
}

function toOriginalRange(normalizedMap: number[], start: number, end: number): { start: number; end: number } | null {
  const originalStart = normalizedMap[start];
  const originalEndInclusive = normalizedMap[end - 1];
  if (originalStart == null || originalEndInclusive == null) return null;
  return {
    start: originalStart,
    end: originalEndInclusive + 1,
  };
}

function extractSentences(content: string): Candidate[] {
  const matches = content.matchAll(/[^.!?\n]+(?:[.!?]+|$)/g);
  return Array.from(matches, (match) => {
    const raw = match[0];
    const leading = raw.match(/^\s*/);
    const trailing = raw.match(/\s*$/);
    const start = (match.index ?? 0) + (leading?.[0].length ?? 0);
    const end = (match.index ?? 0) + raw.length - (trailing?.[0].length ?? 0);
    return {
      text: content.slice(start, end),
      start,
      end,
    };
  }).filter((item) => item.text.trim().length > 0);
}

function extractParagraphs(content: string): Candidate[] {
  const paragraphs: Candidate[] = [];
  const matches = content.matchAll(/[^\n]+(?:\n(?!\n)[^\n]+)*/g);
  for (const match of matches) {
    const text = match[0].trim();
    if (!text) continue;
    const offset = match.index ?? 0;
    const startDelta = match[0].indexOf(text);
    const start = offset + Math.max(0, startDelta);
    paragraphs.push({
      text,
      start,
      end: start + text.length,
    });
  }
  return paragraphs;
}

function pickBestMatch(candidates: ResolvedTextAnchorMatch[]): ResolvedTextAnchorMatch | null {
  if (candidates.length === 0) return null;
  return candidates.sort((left, right) => {
    if (right.confidence !== left.confidence) return right.confidence - left.confidence;
    return left.start - right.start;
  })[0] ?? null;
}

function resolveExactMatch(content: string, anchor: TextAnchor | undefined): ResolvedTextAnchorMatch | null {
  const anchorText = anchor?.text?.trim();
  if (!anchorText) return null;

  const candidates = findAllOccurrences(content, anchorText).map((start) => {
    const end = start + anchorText.length;
    const contextScore = scoreContext(content, start, end, anchor);
    return {
      start,
      end,
      text: content.slice(start, end),
      confidence: 1 + contextScore * CONTEXT_WEIGHT,
      mode: 'exact' as const,
    };
  });

  return pickBestMatch(candidates);
}

function resolveNormalizedExactMatch(content: string, anchor: TextAnchor | undefined): ResolvedTextAnchorMatch | null {
  const anchorText = anchor?.text?.trim();
  if (!anchorText) return null;

  const normalizedContent = normalizeWithMap(content);
  const normalizedAnchor = normalizeForTokens(anchorText);
  if (!normalizedAnchor || !normalizedContent.text) return null;

  const candidates = findAllOccurrences(normalizedContent.text, normalizedAnchor)
    .map((start) => {
      const range = toOriginalRange(normalizedContent.map, start, start + normalizedAnchor.length);
      if (!range) return null;
      const contextScore = scoreContext(content, range.start, range.end, anchor);
      return {
        start: range.start,
        end: range.end,
        text: content.slice(range.start, range.end),
        confidence: 0.92 + contextScore * CONTEXT_WEIGHT,
        mode: 'normalized' as const,
      };
    })
    .filter(Boolean) as ResolvedTextAnchorMatch[];

  return pickBestMatch(candidates);
}

function resolveFuzzyMatch(content: string, anchor: TextAnchor | undefined): ResolvedTextAnchorMatch | null {
  const anchorText = anchor?.text?.trim();
  if (!anchorText) return null;

  const anchorTokens = tokenize(anchorText);
  if (anchorTokens.size === 0) return null;

  const candidates = [
    ...extractSentences(content),
    ...(anchorText.length > 160 ? extractParagraphs(content) : []),
  ];

  const matches = candidates
    .map((candidate) => {
      const tokenScore = computeJaccard(anchorTokens, tokenize(candidate.text));
      const contextScore = scoreContext(content, candidate.start, candidate.end, anchor);
      const lengthRatio = Math.max(anchorText.length, candidate.text.length, 1);
      const lengthScore = 1 - Math.min(1, Math.abs(candidate.text.length - anchorText.length) / lengthRatio);
      const confidence = tokenScore * (1 - CONTEXT_WEIGHT - LENGTH_WEIGHT) + contextScore * CONTEXT_WEIGHT + lengthScore * LENGTH_WEIGHT;
      return {
        start: candidate.start,
        end: candidate.end,
        text: candidate.text,
        confidence,
        mode: 'fuzzy' as const,
      };
    })
    .filter((candidate) => candidate.confidence >= MIN_FUZZY_CONFIDENCE);

  return pickBestMatch(matches);
}

function resolveLegacyOffsets(content: string, anchor: TextAnchor | undefined): ResolvedTextAnchorMatch | null {
  const startOffset = anchor?.startOffset;
  const endOffset = anchor?.endOffset;
  if (startOffset == null || endOffset == null) return null;

  const start = Math.max(0, Math.min(startOffset, content.length));
  const end = Math.max(start, Math.min(endOffset, content.length));
  return {
    start,
    end,
    text: content.slice(start, end),
    confidence: 0.4,
    mode: 'legacy',
  };
}

export function buildTextAnchorContext(
  sourceContent: string | undefined,
  startOffset: number | undefined,
  endOffset: number | undefined,
  contextWindow = DEFAULT_CONTEXT_WINDOW,
): Pick<TextAnchor, 'text' | 'prefixText' | 'suffixText'> | undefined {
  if (!sourceContent || startOffset == null || endOffset == null) return undefined;

  const safeStart = Math.max(0, Math.min(startOffset, sourceContent.length));
  const safeEnd = Math.max(safeStart, Math.min(endOffset, sourceContent.length));
  const text = sourceContent.slice(safeStart, safeEnd);
  if (!text.trim()) return undefined;

  return {
    text,
    prefixText: sourceContent.slice(Math.max(0, safeStart - contextWindow), safeStart),
    suffixText: sourceContent.slice(safeEnd, Math.min(sourceContent.length, safeEnd + contextWindow)),
  };
}

export function resolveTextAnchorSelection(content: string, anchor: TextAnchor | undefined): ResolvedTextAnchorMatch | null {
  const anchorText = anchor?.text?.trim();
  if (!content) return null;

  if (!anchorText) {
    return resolveLegacyOffsets(content, anchor);
  }

  return (
    resolveExactMatch(content, anchor)
    ?? resolveNormalizedExactMatch(content, anchor)
    ?? resolveFuzzyMatch(content, anchor)
  );
}
